// Utility to convert content payload keys to PascalCase for backend
function toPascalCaseContentPayload(content: any, moduleId: string) {
  return {
    ModuleID: moduleId,
    Title: content.title,
    Type: content.type,
    VideoUrl: content.videoURL,
    ImageUrl: content.imageURL,
    AudioUrl: content.audioURL,
    Content: content.content,
    Order: content.order,
    Duration: content.duration,
    // Language: content.language, // Uncomment if needed
  };
}
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Checkbox,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Image as ImageIcon,
  VideoLibrary,
  Save,
  ArrowBack,
  Language,
  Quiz,
} from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import FormikInput from "components/formikInput";
import Loader from "components/loader";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import { AppContext } from "context/appContext";
import { useCourse } from "hooks/useCourse";
import useIndustry from "hooks/useIndustry";
import { courseApi } from "configs/api";
import { assessmentApi } from "configs/api";
import { useDispatch } from "react-redux";
import { clearCurrentCourse } from "slices/courseSlice";
import { fetchAssessmentsByCourse } from "slices/assessmentSlice";
import { CourseModule, ModuleContent, Course } from "slices/courseSlice";
import { ModuleDialog } from "../course/components/ModuleDialog";
import { ContentDialog } from "../course/components/ContentDialog";
import { AssessmentDialog } from "./components/AssessmentDialog";
import FileUpload from "components/FileUpload";
import { UUID } from "crypto";

interface Module extends CourseModule {
  content: Content[];
}

interface Content extends ModuleContent {
  type: "video" | "image_audio";
}

interface Assessment {
  id?: string;
  title: string;
  description?: string;
  moduleID?: string;
  passingScore: number;
  timeLimit?: number;
  unlocksModuleID?: string;
}

const steps = ["Course Info", "Modules & Content", "Assessments", "Pricing & Categories"];

// Category Selector Component
function CategorySelector({
  selectedCategories,
  onChange,
  activeStep
}: {
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
  activeStep: number;
}) {
  const { industries, getAllIndustries } = useIndustry();

  useEffect(() => {
    // if (industries.length === 0) {
      getAllIndustries();
    // }
  }, [activeStep]);

  return (
    <Autocomplete
      multiple
      options={industries || []}
      getOptionLabel={(option: any) => option.name || ""}
      value={(industries || []).filter((ind: any) => selectedCategories.includes(ind._id))}
      onChange={(a, newValue: any) => {
        console.log(a, newValue);
        onChange(newValue.map((v: any) => v._id));
      }}
      loading={industries.length === 0}
      renderInput={(params: any) => (
        <TextField
          {...params}
          placeholder={industries.length === 0 ? "Loading categories..." : "Select categories"}
          variant="outlined"
          size="medium"
        />
      )}
      renderOption={(props: any, option: any, { selected }: any) => (
        <li {...props}>
          <Checkbox checked={selected} />
          {option.name}
        </li>
      )}
      renderTags={(value: any, getTagProps: any) =>
        value.map((option: any, index: number) => (
          <Chip
            {...getTagProps({ index })}
            key={option._id}
            label={option.name}
            size="small"
          />
        ))
      }
    />
  );
}


function CourseBuilder(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Support /courses/create, /courses/:id/edit and /courses/:id/builder
  const { id } = useParams<{ id: string }>();
  const { showAlert } = useContext(AppContext);
  const dispatch = useDispatch();
  const { currentCourse, getCourse, addCourse, editCourse } = useCourse();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState<Module[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [deletedContentIds, setDeletedContentIds] = useState<string[]>([]);
  const [deletedAssessmentIds, setDeletedAssessmentIds] = useState<string[]>([]);
  const [openModuleDialog, setOpenModuleDialog] = useState(false);
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [openAssessmentDialog, setOpenAssessmentDialog] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [editingContent, setEditingContent] = useState<{ moduleIndex: number; content: Content } | null>(null);
  const [editingAssessment, setEditingAssessment] = useState<Assessment | null>(null);
  const [selectedModuleIndex, setSelectedModuleIndex] = useState<number | null>(null);
  const [openAIPrompt, setOpenAIPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [createdCourseId, setCreatedCourseId] = useState<string | null>(id || null);

  const normalizeAssessmentList = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  const isEditMode = !!id;

  // Check if we're returning from assessment builder and restore step/state
  useEffect(() => {
    const state = location.state as any;
    if (state?.activeStep !== undefined) {
      setActiveStep(state.activeStep);
    }
    
    // If returning from assessment builder, preserve state and reload course/assessments
    if (state?.fromAssessment) {
      const courseId = createdCourseId || id;
      if (courseId) {
        // Reload course data
        if (isEditMode && id) {
          getCourse(id);
        }
        // Fetch assessments for the course
        dispatch(fetchAssessmentsByCourse(courseId) as any).then((result: any) => {
          const fetchedAssessments = normalizeAssessmentList(result?.payload);
          // Update local assessments state with fetched assessments
          setAssessments(
            fetchedAssessments.map((a: any) => ({
              id: a.id,
              title: a.title,
              description: a.description,
              moduleID: a.moduleID,
              passingScore: a.passingScore,
              timeLimit: a.timeLimit,
              unlocksModuleID: a.unlocksModuleID,
            }))
          );
        }).catch((error: any) => {
          console.error("Failed to fetch assessments:", error);
        });
      }
      // Don't clear state when returning from assessment builder
      return;
    }
  }, [location.state, isEditMode, id, getCourse, createdCourseId, dispatch]);
  useEffect(() => {
    if (isEditMode && id) {
      getCourse(id);
    } else {
      // Only clear state if we're not returning from assessment builder
      const state = location.state as any;
      if (!state?.fromAssessment) {
        setModules([]);
        setAssessments([]);
        setCreatedCourseId(null);
        setActiveStep(0);
        // Clear Redux state
        dispatch(clearCurrentCourse());
      }
    }
  }, [id, isEditMode, getCourse, dispatch, location.state]);

  useEffect(() => {
    if (currentCourse && currentCourse.modules) {
      setModules(
        currentCourse.modules.map((m: any) => ({
          ...m,
          content: m.content || [],
        }))
      );
    } else if (!currentCourse && isEditMode) {
      // Clear modules if course is cleared
      setModules([]);
    }
    
    // Debug: Log categoryID when course loads
    if (currentCourse && isEditMode) {
      console.log('Course loaded - categoryID:', currentCourse.categoryID);
    }
  }, [currentCourse, isEditMode]);

  // Load assessments when course is loaded in edit mode
  useEffect(() => {
    const courseId = createdCourseId || id;
    if (courseId && isEditMode && currentCourse) {
      // Fetch assessments for the course
      dispatch(fetchAssessmentsByCourse(courseId) as any).then((result: any) => {
        const fetchedAssessments = normalizeAssessmentList(result?.payload);
        // Update local assessments state with fetched assessments
        setAssessments(
          fetchedAssessments.map((a: any) => ({
            id: a.id,
            title: a.title,
            description: a.description,
            moduleID: a.moduleID,
            passingScore: a.passingScore,
            timeLimit: a.timeLimit,
            unlocksModuleID: a.unlocksModuleID,
          }))
        );
      }).catch((error: any) => {
        console.error("Failed to fetch assessments:", error);
      });
    }
  }, [currentCourse, isEditMode, id, createdCourseId, dispatch]);

  // Load assessments when entering step 3, even if currentCourse isn't hydrated in Redux
  useEffect(() => {
    const courseId = createdCourseId || id;
    if (activeStep !== 2 || !courseId) {
      return;
    }

    dispatch(fetchAssessmentsByCourse(courseId) as any)
      .then((result: any) => {
        const fetchedAssessments = normalizeAssessmentList(result?.payload);
        setAssessments(
          fetchedAssessments.map((a: any) => ({
            id: a.id,
            title: a.title,
            description: a.description,
            moduleID: a.moduleID,
            passingScore: a.passingScore,
            timeLimit: a.timeLimit,
            unlocksModuleID: a.unlocksModuleID,
          }))
        );
      })
      .catch((error: any) => {
        console.error("Failed to fetch assessments:", error);
      });
  }, [activeStep, id, createdCourseId, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all state when component unmounts
      setModules([]);
      setAssessments([]);
      setCreatedCourseId(null);
      setActiveStep(0);
      setOpenModuleDialog(false);
      setOpenContentDialog(false);
      setOpenAssessmentDialog(false);
      setEditingModule(null);
      setEditingContent(null);
      setEditingAssessment(null);
      setSelectedModuleIndex(null);
    };
  }, []);

  const handleNext = async () => {
    // Save modules when moving from step 1 to step 2
    if (activeStep === 1 && (createdCourseId || id)) {
      await saveModulesAndContent(createdCourseId || id || "");
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const saveModulesAndContent = async (courseId: string) => {
    if (!courseId || modules.length === 0) return;

    for (const module of modules) {
      try {
        let moduleId = module.id;
        
        // Only create module if it doesn't have an ID (new module)
        if (!moduleId) {
          const moduleResponse = await courseApi.post(`/api/course-modules/`, {
            courseID: courseId,
            title: module.title,
            description: module.description,
            order: module.order,
            isUnlocked: module.isUnlocked,
            unlockCondition: module.unlockCondition,
          });

          if (moduleResponse.data.status && moduleResponse.data.data) {
            moduleId = moduleResponse.data.data.id;
            // Update local module with new ID
            const moduleIndex = modules.findIndex((m) => m === module);
            if (moduleIndex !== -1) {
              const updatedModules = [...modules];
              updatedModules[moduleIndex] = { ...module, id: moduleId };
              setModules(updatedModules);
            }
          }
        }

        // Save content for this module
        if (moduleId && module.content && module.content.length > 0) {
          for (const content of module.content) {
            try {
              // Skip content that's marked for deletion
              if (content.id && deletedContentIds.includes(content.id)) {
                continue;
              }

              // Always convert to PascalCase for backend
              const payload = toPascalCaseContentPayload(content, moduleId);

              if (content.id) {
                // Update existing content
                await courseApi.put(`/api/courses/content/${content.id}`, payload);
              } else {
                // Create new content
                const contentResponse = await courseApi.post(`/api/courses/modules/${moduleId}/content`, payload);
                if (contentResponse.data.status && contentResponse.data.data) {
                  // Update local content with new ID
                  const moduleIndex = modules.findIndex((m) => m.id === moduleId);
                  if (moduleIndex !== -1) {
                    const contentIndex = modules[moduleIndex].content.findIndex((c) => c === content);
                    if (contentIndex !== -1) {
                      const updatedModules = [...modules];
                      updatedModules[moduleIndex].content[contentIndex] = {
                        ...content,
                        id: contentResponse.data.data.id,
                      };
                      setModules(updatedModules);
                    }
                  }
                }
              }
            } catch (contentError: any) {
              console.error("Error saving content:", contentError);
            }
          }
        }
        
        // Delete content that was removed (check if any content in database is not in local state)
        // This is handled by tracking deleted content separately
      } catch (moduleError: any) {
        console.error("Error saving module:", moduleError);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAddModule = () => {
    setEditingModule(null);
    setOpenModuleDialog(true);
  };

  const handleEditModule = (index: number) => {
    setEditingModule(modules[index]);
    setSelectedModuleIndex(index);
    setOpenModuleDialog(true);
  };

  const handleDeleteModule = (index: number) => {
    const newModules = modules.filter((_, i) => i !== index);
    setModules(newModules.map((m, i) => ({ ...m, order: i })));
  };

  const handleSaveModule = (moduleData: Partial<Module>) => {
    if (editingModule && selectedModuleIndex !== null) {
      const updated = [...modules];
      updated[selectedModuleIndex] = {
        ...updated[selectedModuleIndex],
        ...moduleData,
        content: updated[selectedModuleIndex].content,
      };
      setModules(updated);
    } else {
      const newModule: Module = {
        title: moduleData.title || "",
        description: moduleData.description,
        order: modules.length,
        isUnlocked: moduleData.isUnlocked ?? (modules.length === 0),
        unlockCondition: moduleData.unlockCondition || "sequential",
        content: [],
      };
      setModules([...modules, newModule]);
    }
    setOpenModuleDialog(false);
    setEditingModule(null);
    setSelectedModuleIndex(null);
  };

  const handleAddContent = (moduleIndex: number) => {
    setSelectedModuleIndex(moduleIndex);
    setEditingContent(null);
    setOpenContentDialog(true);
  };

  const handleEditContent = (moduleIndex: number, contentIndex: number) => {
    setSelectedModuleIndex(moduleIndex);
    setEditingContent({ moduleIndex, content: modules[moduleIndex].content[contentIndex] });
    setOpenContentDialog(true);
  };

  const handleSaveContent = (contentData: Partial<Content>) => {
    if (!selectedModuleIndex && selectedModuleIndex !== 0) return;

    const updated = modules.map((module, idx) => {
      if (idx === selectedModuleIndex) {
        // Create a new module object with a new content array
        const newContentArray = [...module.content];
        
        if (editingContent && editingContent.moduleIndex === selectedModuleIndex) {
          // Update existing content
          const contentIndex = newContentArray.findIndex((c) => c.id === editingContent.content.id);
          if (contentIndex !== -1) {
            newContentArray[contentIndex] = {
              ...contentData,
              id: editingContent.content.id,
            } as Content;
          }
        } else {
          // Add new content
          const newContent: Content = {
            title: contentData.title || "",
            type: contentData.type || "video",
            videoURL: contentData.videoURL,
            imageURL: contentData.imageURL,
            audioURL: contentData.audioURL,
            order: newContentArray.length,
            // language: contentData.language || "en",
            duration: contentData.duration,
          };
          newContentArray.push(newContent);
        }
        
        return {
          ...module,
          content: newContentArray,
        };
      }
      return module;
    });
    
    setModules(updated);
    setOpenContentDialog(false);
    setEditingContent(null);
    setSelectedModuleIndex(null);
  };

  const handleDeleteContent = (moduleIndex: number, contentIndex: number) => {
    const contentToDelete = modules[moduleIndex]?.content[contentIndex];
    if (contentToDelete?.id) {
      // Track deleted content ID for backend deletion
      setDeletedContentIds([...deletedContentIds, contentToDelete.id]);
    }
    
    const updated = modules.map((module, idx) => {
      if (idx === moduleIndex) {
        return {
          ...module,
          content: module.content.filter((_, i) => i !== contentIndex),
        };
      }
      return module;
    });
    setModules(updated);
  };

  const handleAddAssessment = () => {
    setEditingAssessment(null);
    setOpenAssessmentDialog(true);
  };

  const handleSaveAssessment = (assessmentData: Partial<Assessment>) => {
    if (editingAssessment) {
      setAssessments(assessments.map((a) => (a.id === editingAssessment.id ? { ...a, ...assessmentData } : a)));
    } else {
      const newAssessment: Assessment = {
        title: assessmentData.title || "",
        description: assessmentData.description,
        moduleID: assessmentData.moduleID,
        passingScore: assessmentData.passingScore || 70,
        timeLimit: assessmentData.timeLimit,
        unlocksModuleID: assessmentData.unlocksModuleID,
      };
      setAssessments([...assessments, newAssessment]);
    }
    setOpenAssessmentDialog(false);
    setEditingAssessment(null);
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      showAlert("Please enter a prompt", "error", "AI Generation");
      return;
    }

    setAiLoading(true);
    try {
      // TODO: Integrate OpenAI API
      // For now, just show a placeholder
      showAlert("AI generation feature coming soon. Please enter titles manually.", "info", "AI Generation");
      setOpenAIPrompt(false);
      setAiPrompt("");
    } catch (error: any) {
      showAlert(error.message || "Failed to generate content", "error", "AI Generation");
    } finally {
      setAiLoading(false);
    }
  };

  const handleFinalSubmit = async (values: any) => {
    setLoading(true);
    try {
      console.log(values);
      // return;
      // Format custom quota tiers into { seats: totalPrice } map
      const quotaPricing: Record<string, number> = {};
      const customQuotas = Array.isArray(values.customQuotas) ? values.customQuotas : [];
      for (const row of customQuotas) {
        const seats = parseInt(String(row?.seats ?? ""), 10);
        const tierPrice = parseFloat(String(row?.price ?? "")) || 0;
        if (Number.isInteger(seats) && seats > 0 && tierPrice > 0) {
          quotaPricing[String(seats)] = tierPrice;
        }
      }
      
      // Ensure price is a number
      const price = typeof values.price === 'string' ? parseFloat(values.price) || 0 : (values.price || 0);
      
      // Extract categoryID - ensure it's always an array for backend
      const categoryID = Array.isArray(values.categoryID) ? values.categoryID : (values.categoryID ? [values.categoryID] : []);
      
      const courseData: any = {
        title: values.title,
        description: values.description,
        status: values.status,
        price: price,
        isFree: values.isFree,
        quotaPricing: Object.keys(quotaPricing).length > 0 ? quotaPricing : undefined,
        categoryID: categoryID.length > 0 ? categoryID : [], // Always send as array
        thumbnailURL: values.thumbnailURL,
      };
      
      // Debug: Log what we're sending
      console.log('Saving course with categoryID:', categoryID);
      console.log('Full courseData:', JSON.stringify(courseData, null, 2));

      let courseId = createdCourseId || id;
      
      // If creating new course and we haven't created it yet, create it first
      if (!isEditMode && !courseId && activeStep === 0) {
        const course = await addCourse(courseData);
        console.log('reached here')
        console.log(course)
        if (course && course.data.id) {
          courseId = course.data.id;
          setCreatedCourseId(courseId || null);
          showAlert("Course created! Now add modules and content.", "success", "Success");
          setActiveStep(1); // Move to modules step
          setLoading(false);
          return;
        } else {
          throw new Error("Failed to create course");
        }
      }
      
      if (isEditMode && id) {
        await editCourse(id, courseData);
        courseId = id;
      } else if (!courseId) {
        throw new Error("Course ID is required");
      }

      // Save modules and their content (only on final step or when explicitly saving)
      // Note: Deletions are handled inside saveModulesAndContent
      if (courseId && modules.length > 0 && activeStep >= 1) {
        await saveModulesAndContent(courseId);
      }

      // Delete removed assessments from backend
      if (deletedAssessmentIds.length > 0) {
        for (const assessmentId of deletedAssessmentIds) {
          try {
            await assessmentApi.delete(`/api/assessments/${assessmentId}`);
          } catch (error: any) {
            console.error(`Error deleting assessment ${assessmentId}:`, error);
          }
        }
        setDeletedAssessmentIds([]);
      }

      // Save assessments (create new ones and update existing ones)
      if (assessments.length > 0) {
        for (const assessment of assessments) {
          try {
            if (assessment.id) {
              // Update existing assessment
              await assessmentApi.put(`/api/assessments/${assessment.id}`, {
                moduleID: assessment.moduleID,
                title: assessment.title,
                description: assessment.description,
                passingScore: assessment.passingScore,
                timeLimit: assessment.timeLimit,
                unlocksModuleID: assessment.unlocksModuleID,
              });
            } else {
              // Create new assessment
              const assessmentResponse = await assessmentApi.post("/api/assessments", {
                courseID: courseId,
                moduleID: assessment.moduleID,
                title: assessment.title,
                description: assessment.description,
                passingScore: assessment.passingScore,
                timeLimit: assessment.timeLimit,
                unlocksModuleID: assessment.unlocksModuleID,
              });
              
              // Update local assessment with new ID
              if (assessmentResponse.data?.status && assessmentResponse.data?.data?._id) {
                setAssessments(assessments.map((a) => 
                  a === assessment ? { ...a, _id: assessmentResponse.data.data.id } : a
                ));
              }
            }
          } catch (assessmentError: any) {
            console.error("Error saving assessment:", assessmentError);
            showAlert(`Failed to save assessment: ${assessment.title}`, "warning", "Warning");
          }
        }
      }

      showAlert(`Course ${isEditMode ? "updated" : "created"} successfully!`, "success", "Success");
      
      // Only navigate away if it's the final step, otherwise stay in builder and move to next step
      const totalSteps = 4; // ["Course Info", "Modules & Content", "Assessments", "Pricing & Categories"]
      if (activeStep === totalSteps - 1) {
        navigate(`/courses/${courseId}`);
      } else {
        // Move to next step if not on final step
        setActiveStep((prev) => Math.min(prev + 1, totalSteps - 1));
      }
    } catch (error: any) {
      showAlert(error.message || "Failed to save course", "error", "Error");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode && !currentCourse) {
    return <Loader loading={true} />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Box mb={3} display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/courses")}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {isEditMode ? "Edit Course" : "Create Course"}
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Formik
              key={isEditMode ? `edit-${id}` : 'create'}
              enableReinitialize={true}
              initialValues={{
                title: isEditMode && currentCourse ? currentCourse.title : "",
                description: isEditMode && currentCourse ? currentCourse.description : "",
                status: isEditMode && currentCourse ? currentCourse.status : "draft",
                price: isEditMode && currentCourse ? currentCourse.price : 0,
                isFree: isEditMode && currentCourse ? (currentCourse.isFree ?? false) : false,
                customQuotas: isEditMode && currentCourse
                  ? Object.entries(currentCourse.quotaPricing || {})
                      .sort((a, b) => Number(a[0]) - Number(b[0]))
                      .map(([seats, tierPrice]) => ({
                        seats: String(seats),
                        price: String(tierPrice),
                      }))
                  : [{ seats: "1", price: "" }, { seats: "5", price: "" }, { seats: "10", price: "" }],
                categoryID: isEditMode && currentCourse ? (Array.isArray(currentCourse.categoryID) ? currentCourse.categoryID : (currentCourse.categoryID ? [currentCourse.categoryID] : [])) : [],
                thumbnailURL: isEditMode && currentCourse ? currentCourse.thumbnailURL : "",
                thumbnailFile: null,
              }}
              validationSchema={Yup.object({
                title: Yup.string().required("Title is required"),
                description: Yup.string().optional(),
              })}
              onSubmit={handleFinalSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  {activeStep === 0 && (
                    <Box>
                      <Typography variant="h6" mb={3} fontWeight="bold">
                        Step 1: Basic Course Information
                      </Typography>
                      <Typography variant="body2" color="text" mb={3}>
                        Start by providing basic information about your course. You&apos;ll add modules and content in the next steps.
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormikInput 
                            name="title" 
                            label="Course Title *" 
                            placeholder="e.g., Introduction to React" 
                            size="large" 
                          />
                          <Typography variant="caption" color="text" mt={0.5} display="block">
                            Enter a clear and descriptive title for your course
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <FormikInput
                            name="description"
                            label="Course Description"
                            placeholder="Describe what students will learn in this course..."
                            multiline
                            rows={4}
                            size="large"
                          />
                          <Typography variant="caption" color="text" mt={0.5} display="block">
                            Provide a detailed description of the course content and learning objectives
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormikInput
                            name="status"
                            type="dropdown"
                            label="Course Status *"
                            options={[
                              { value: "draft", label: "Draft (Not visible to students)" },
                              { value: "published", label: "Published (Visible to students)" },
                            ]}
                            size="large"
                          />
                          <Typography variant="caption" color="text" mt={0.5} display="block">
                            Draft courses are only visible to you. Published courses are available to students.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Typography variant="caption" fontWeight="medium" mb={1} display="block">
                              Course Thumbnail
                            </Typography>
                            <FileUpload
                              type="image"
                              onUploadComplete={(url) => {
                                if (typeof url === 'string') {
                                  setFieldValue('thumbnailURL', url);
                                }
                              }}
                              existingUrl={values.thumbnailURL}
                              label="Upload Thumbnail Image"
                            />
                          </Box>
                          <FormikInput
                            name="thumbnailURL"
                            label="Or Enter Thumbnail URL"
                            placeholder="https://example.com/thumbnail.jpg"
                            size="large"
                          />
                          <Typography variant="caption" color="text" mt={0.5} display="block">
                            Upload an image file or enter a URL for the course thumbnail (optional)
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {activeStep === 1 && (
                    <Box>
                      <Typography variant="h6" mb={1} fontWeight="bold">
                        Step 2: Course Modules & Content
                      </Typography>
                      <Typography variant="body2" color="text" mb={3}>
                        Organize your course into modules. Each module can contain multiple videos or image+audio content. 
                        You can add content in multiple languages for each module.
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Course Modules ({modules.length})
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            onClick={() => setOpenAIPrompt(true)}
                          >
                            <Language sx={{ mr: 1 }} /> AI Generate Titles
                          </Button>
                          <Button variant="gradient" color="info" size="small" onClick={handleAddModule}>
                            <Add sx={{ mr: 1 }} /> Add Module
                          </Button>
                        </Box>
                      </Box>

                      {modules.length === 0 ? (
                        <Box textAlign="center" py={4}>
                          <Typography variant="body1" color="text" mb={2}>
                            No modules yet. Add your first module to get started.
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {modules.map((module, moduleIndex) => (
                            <Card key={moduleIndex} sx={{ mb: 2 }}>
                              <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                                  <Box>
                                    <Typography variant="h6">{module.title}</Typography>
                                    {module.description && (
                                      <Typography variant="body2" color="text">
                                        {module.description}
                                      </Typography>
                                    )}
                                    <Box display="flex" gap={1} mt={1}>
                                      <Chip
                                        label={`Order: ${module.order + 1}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={module.unlockCondition}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                      />
                                    </Box>
                                  </Box>
                                  <Box display="flex" gap={1}>
                                    <IconButton size="small" onClick={() => handleEditModule(moduleIndex)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteModule(moduleIndex)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>

                                <Box mt={2}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2" fontWeight="medium">
                                      Module Content ({module.content.length})
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      color="info"
                                      size="small"
                                      onClick={() => handleAddContent(moduleIndex)}
                                    >
                                      <Add sx={{ mr: 0.5, fontSize: 16 }} /> Add Content
                                    </Button>
                                  </Box>
                                  <Typography variant="caption" color="text" mb={1} display="block">
                                    Add videos or images with audio. You can add multiple content items in different languages.
                                  </Typography>

                                  {module.content.length === 0 ? (
                                    <Typography variant="caption" color="text">
                                      No content yet
                                    </Typography>
                                  ) : (
                                    <Box display="flex" flexDirection="column" gap={1}>
                                      {module.content.map((content, contentIndex) => (
                                        <Box
                                          key={contentIndex}
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          p={1}
                                          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                                        >
                                          <Box display="flex" alignItems="center" gap={1}>
                                            {content.type === "video" ? (
                                              <VideoLibrary fontSize="small" />
                                            ) : (
                                              <ImageIcon fontSize="small" />
                                            )}
                                            <Typography variant="body2">{content.title}</Typography>
                                            {/* <Chip label={content.language} size="small" /> */}
                                          </Box>
                                          <Box display="flex" gap={0.5}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleEditContent(moduleIndex, contentIndex)}
                                            >
                                              <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDeleteContent(moduleIndex, contentIndex)}
                                            >
                                              <Delete fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                  {activeStep === 2 && (
                    <Box>
                      <Typography variant="h6" mb={1} fontWeight="bold">
                        Step 3: Assessments & Module Unlocking
                      </Typography>
                      <Typography variant="body2" color="text" mb={3}>
                        Create MCQ assessments to test student knowledge. Assessments can unlock the next module when passed.
                        You can add multiple questions to each assessment.
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          Course Assessments ({assessments.length})
                        </Typography>
                        <Button variant="gradient" color="info" size="small" onClick={handleAddAssessment}>
                          <Add sx={{ mr: 1 }} /> Add Assessment
                        </Button>
                      </Box>

                      {assessments.length === 0 ? (
                        <Box textAlign="center" py={4}>
                          <Typography variant="body1" color="text">
                            No assessments yet. Add assessments to unlock modules.
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {assessments.map((assessment, index) => (
                            <Card key={index} sx={{ mb: 2 }}>
                              <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="start">
                                  <Box>
                                    <Typography variant="h6">{assessment.title}</Typography>
                                    {assessment.description && (
                                      <Typography variant="body2" color="text">
                                        {assessment.description}
                                      </Typography>
                                    )}
                                    <Box display="flex" gap={1} mt={1}>
                                      <Chip
                                        label={`Passing: ${assessment.passingScore}%`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                      {assessment.timeLimit && (
                                        <Chip
                                          label={`Time: ${assessment.timeLimit} min`}
                                          size="small"
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  <Box display="flex" gap={1}>
                                    <Button
                                      variant="outlined"
                                      color="info"
                                      size="small"
                                      onClick={async () => {
                                        const courseId = createdCourseId || id;
                                        if (!courseId) {
                                          showAlert("Please save the course first", "warning", "Warning");
                                          return;
                                        }

                                        let assessmentId = assessment.id;

                                        // Validate assessment has required fields
                                        if (!assessment.title || assessment.title.trim() === "") {
                                          showAlert("Assessment must have a title before adding questions", "warning", "Warning");
                                          return;
                                        }

                                        // If assessment doesn't have an id, save it first
                                        if (!assessmentId) {
                                          try {
                                            console.log("Saving new assessment before adding questions:", assessment);
                                            // Prepare assessment data, filtering out empty strings
                                            const assessmentData: any = {
                                              courseID: courseId,
                                              title: assessment.title.trim(),
                                              type: "mcq",
                                              passingScore: assessment.passingScore || 70,
                                              order: 0,
                                              moduleID: (assessment.moduleID && assessment.moduleID !== "0") ? assessment.moduleID : undefined,
                                            };
                                            
                                            // Only include optional fields if they have values
                                            if (assessment.moduleID && assessment.moduleID.trim() !== "" && assessment.moduleID !== "0") {
                                              assessmentData.moduleID = assessment.moduleID;
                                            }
                                            if (assessment.description && assessment.description.trim() !== "") {
                                              assessmentData.description = assessment.description;
                                            }
                                            if (assessment.timeLimit && assessment.timeLimit > 0) {
                                              assessmentData.timeLimit = assessment.timeLimit;
                                            }
                                            if (assessment.unlocksModuleID && assessment.unlocksModuleID.trim() !== "" && assessment.unlocksModuleID !== "0") {
                                              assessmentData.unlocksModuleID = assessment.unlocksModuleID;
                                            }
                                            
                                            console.log("Saving assessment with data:", assessmentData);
                                            const response = await assessmentApi.post("/api/assessments", assessmentData);
                                            // console.log("Assessment save response:", response.data);
                                            
                                            if (response.data?.id) {
                                              assessmentId = response.data.id;
                                              // Update the assessment in local state with the new id
                                              setAssessments(assessments.map((a, idx) => 
                                                idx === index ? { ...a, id: assessmentId } : a
                                              ));
                                              showAlert("Assessment saved. You can now add questions.", "success", "Success");
                                            } else {
                                              const errorMsg = response.data?.error || "Failed to save assessment";
                                              console.error("Assessment save response:", response.data);
                                              showAlert(errorMsg, "error", "Error");
                                              return;
                                            }
                                          } catch (error: any) {
                                            const errorMsg = error.response?.data?.error || error.message || "Failed to save assessment";
                                            console.error("Assessment save error:", error);
                                            console.error("Error response:", error.response?.data);
                                            showAlert(errorMsg, "error", "Error");
                                            return;
                                          }
                                        }
                                        
                                        if (assessmentId) {
                                          navigate(`/courses/${courseId}/assessments/${assessmentId}`, {
                                            state: { fromBuilder: true }
                                          });
                                        }
                                      }}
                                      sx={{ minWidth: 'auto', px: 1.5 }}
                                    >
                                      <Quiz sx={{ mr: 0.5, fontSize: '16px' }} /> Questions
                                    </Button>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingAssessment(assessment);
                                        setOpenAssessmentDialog(true);
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete "${assessment.title}"?`)) {
                                          if (assessment.id) {
                                            // Track deleted assessment ID for backend deletion
                                            setDeletedAssessmentIds([...deletedAssessmentIds, assessment.id]);
                                          }
                                          setAssessments(assessments.filter((_, i) => i !== index));
                                        }
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {activeStep === 3 && (
                    <Box>
                      <Typography variant="h6" mb={1} fontWeight="bold">
                        Step 4: Pricing & Categories
                      </Typography>
                      <Typography variant="body2" color="text" mb={3}>
                        Set the course price for individual purchases and quota-based pricing for companies. Free courses are available to all users.
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <FormikInput
                            name="price"
                            type="number"
                            label="Single Course Price ($) *"
                            placeholder="0.00"
                            size="large"
                          />
                          <Typography variant="caption" color="text" mt={0.5} display="block">
                            Price for a single course purchase by individual users
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormikInput
                            name="isFree"
                            type="checkbox"
                            label="Mark as Free Course"
                            size="large"
                          />
                          <Typography variant="caption" color="text" mt={0.5} display="block">
                            Free courses are available to all users without payment. Overrides all pricing.
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" fontWeight="medium" mb={2}>
                            Quota-Based Pricing (for Companies)
                          </Typography>
                          <Typography variant="body2" color="text" mb={2}>
                            Add as many custom quota tiers as you want. For each tier, set total seats and total price.
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={2}>
                            {(values.customQuotas || []).map((row: any, index: number) => (
                              <Grid container spacing={2} key={`quota-row-${index}`} alignItems="center">
                                <Grid item xs={12} md={4}>
                                  <FormikInput
                                    name={`customQuotas.${index}.seats`}
                                    type="number"
                                    label="Seats"
                                    placeholder="e.g. 25"
                                    size="large"
                                  />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <FormikInput
                                    name={`customQuotas.${index}.price`}
                                    type="number"
                                    label="Total Price ($)"
                                    placeholder="e.g. 900"
                                    size="large"
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => {
                                      const nextRows = [...(values.customQuotas || [])];
                                      nextRows.splice(index, 1);
                                      setFieldValue("customQuotas", nextRows.length > 0 ? nextRows : [{ seats: "", price: "" }]);
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </Grid>
                              </Grid>
                            ))}

                            <Box>
                              <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={() => {
                                  setFieldValue("customQuotas", [
                                    ...(values.customQuotas || []),
                                    { seats: "", price: "" },
                                  ]);
                                }}
                              >
                                Add Quota Tier
                              </Button>
                            </Box>
                          </Box>

                          <Box mt={3}>
                            <Typography variant="caption" color="text" mt={0.5} display="block">
                              For seat counts not listed in these tiers, pricing falls back to base course price × requested seats.
                            </Typography>
                          </Box>

                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" fontWeight="medium" mb={2}>
                            Course Categories *
                          </Typography>
                          <CategorySelector
                            selectedCategories={Array.isArray(values.categoryID) ? values.categoryID : (values.categoryID ? [values.categoryID] : [])}
                            onChange={(categories) => {
                              console.log(categories);
                              setFieldValue("categoryID", categories);
                            }}
                            activeStep={activeStep}
                          />
                          <Typography variant="caption" color="text" mt={1} display="block">
                            Select one or more categories for this course
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Box display="flex" justifyContent="space-between" mt={4}>
                    <Button
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      variant="outlined"
                      color="secondary"
                    >
                      Back
                    </Button>
                    {activeStep === 0 ? (
                      <Button 
                        type="button" 
                        variant="gradient" 
                        color="info" 
                        loading={loading}
                        onClick={async (e) => {
                          e.preventDefault();
                          // Validate step 0
                          if (!values.title) {
                            showAlert("Course title is required", "error", "Validation Error");
                            return;
                          }
                          await handleFinalSubmit(values);
                        }}
                      >
                        {isEditMode || createdCourseId ? "Save & Continue" : "Create Course & Continue"}
                      </Button>
                    ) : activeStep === steps.length - 1 ? (
                      <Button 
                        type="button" 
                        variant="gradient" 
                        color="info" 
                        loading={loading}
                        onClick={async (e) => {
                          e.preventDefault();
                          await handleFinalSubmit(values);
                        }}
                      >
                        <Save sx={{ mr: 1 }} /> {isEditMode ? "Update Course" : "Save Course"}
                      </Button>
                    ) : (
                      <Button onClick={handleNext} variant="gradient" color="info">
                        Next
                      </Button>
                    )}
                  </Box>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Box>
      <Footer />

      {/* Module Dialog */}
      <ModuleDialog
        open={openModuleDialog}
        onClose={() => {
          setOpenModuleDialog(false);
          setEditingModule(null);
          setSelectedModuleIndex(null);
        }}
        onSave={handleSaveModule}
        module={editingModule}
      />

      {/* Content Dialog */}
      <ContentDialog
        open={openContentDialog}
        onClose={() => {
          setOpenContentDialog(false);
          setEditingContent(null);
          setSelectedModuleIndex(null);
        }}
        onSave={handleSaveContent}
        content={editingContent?.content}
      />

      {/* Assessment Dialog */}
      <AssessmentDialog
        open={openAssessmentDialog}
        onClose={() => {
          setOpenAssessmentDialog(false);
          setEditingAssessment(null);
        }}
        onSave={handleSaveAssessment}
        assessment={editingAssessment}
        modules={modules}
      />

      {/* AI Prompt Dialog */}
      <Dialog open={openAIPrompt} onClose={() => setOpenAIPrompt(false)} maxWidth="sm" fullWidth>
        <DialogTitle>AI Content Generation</DialogTitle>
        <DialogContent>
          <FormikInput
            name="prompt"
            label="Enter your prompt"
            placeholder="e.g., Generate 5 module titles for a React course"
            multiline
            rows={4}
            value={aiPrompt}
            onChange={(e: any) => setAiPrompt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAIPrompt(false)}>Cancel</Button>
          <Button onClick={handleAIGenerate} variant="gradient" color="info" loading={aiLoading}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default CourseBuilder;

